import Config from "../services/config";

interface BaseSettings extends RequestInit {
    headers?: Record<string, string>;
}

const baseSettings: BaseSettings = {
    credentials: "include"
};

const postHeaders: Record<string, string> = {
    "Content-Type": "application/x-www-form-urlencoded"
};

const authToken = window.safeSessionStorage.getItem('SF-Auth-Token');

if (authToken) {
    baseSettings.headers = { 'X-Auth-Token': authToken };
    postHeaders['X-Auth-Token'] = authToken;
}

/**
 * Funzione di utilità per aggiornare i token globali dalle risposte
 */
const updateTokenFromResponse = (res: Response): void => {
    const newToken = res.headers.get('X-Auth-Token');
    if (newToken !== null) {
        baseSettings.headers = { ...baseSettings.headers, 'X-Auth-Token': newToken };
        postHeaders['X-Auth-Token'] = newToken;
        window.safeSessionStorage.setItem('SF-Auth-Token', newToken);
    }
};

interface RequestOptions extends RequestInit {
    url: string;
    json?: boolean;
    body?: any;
}

const promiseRequest = (options: RequestOptions): Promise<Response> => {
    let publickeyParam = '';

    if (Config.publickey && options.url.indexOf('public=') === -1) {
        publickeyParam = (options.url.indexOf('?') !== -1 ? '&' : '?') + 'public=' + Config.publickey;
    }

    return fetch(options.url + publickeyParam, { ...baseSettings, ...options });
};

const callbackify = <T>(promise: Promise<T>, cb: (err: any, data: T | false) => void): Promise<void> => 
    promise.then(j => [false, j]).catch(e => [e, false]).then(data => cb(data[0], data[1]));

const toArrayNotation = (base: string | number, ...rest: (string | number)[]): string => 
    base + '' + rest.map(S => '[' + encodeURIComponent(S) + ']').join('');

/**
 * Serializza oggetti complessi in notazione bracket (es. prefix[key]=value)
 */
export const serialize = (obj: any, prefix: (string | number)[] = []): string => {
    if (typeof obj === 'string') return obj;

    return Object.entries(obj)
        .map(([k, v]) => {
            const currentPath = prefix.concat([k]);
            if (v !== null && typeof v === 'object') {
                return serialize(v, currentPath);
            }

            // Replicazione esatta di: toArrayNotation(...prefix, k)
            const [base, ...rest] = currentPath;
            return toArrayNotation(base, ...rest) + '=' + encodeURIComponent(String(v));
        })
        .join('&');
};

/**
 * GET JSON
 */
async function promiseGetJSON<T = any>(url: string, opts: Partial<RequestOptions> = {}): Promise<T> {
    const res = await promiseRequest({ ...opts, url, method: "GET" });
    updateTokenFromResponse(res);
    return await res.json();
}

/**
 * POST
 */
async function promisePost<T = any>(url: string, opts: Partial<RequestOptions> = {}): Promise<T> {
    const res = await promiseRequest({
        ...opts,
        method: "POST",
        url,
        body: serialize(opts.body || {}),
        headers: { ...postHeaders, ...(opts.headers as Record<string, string> || {}) }
    });
    
    updateTokenFromResponse(res);
    return await (opts.json ? res.json() : res.text());
}

export default {
    getJSON: <T = any>(url: string, cb: (err: any, data: T | false) => void, opts?: Partial<RequestOptions>) => 
        callbackify(promiseGetJSON<T>(url, opts), cb),

    get: (url: string, opts: Partial<RequestOptions>, cb: (err: any, data: any) => void) => 
        callbackify(
            promiseRequest({ ...opts, method: "GET", url })
                .then(async (res) => {
                    updateTokenFromResponse(res);
                    return opts.json ? await res.json() : await res.text();
                }),
            cb
        ),

    post: <T = any>(url: string, opts: Partial<RequestOptions>, cb: (err: any, data: T | false) => void) => 
        callbackify(promisePost<T>(url, opts), cb),

    serialize,
    promiseGetJSON,
    promisePost,
    promisePostJSON: <T = any>(url: string, opts: Partial<RequestOptions> = {}) => 
        promisePost<T>(url, { ...opts, json: true })
};
